/**
 * Retry wrapper for Supabase calls with exponential backoff (n8n pattern).
 * Wraps supabase.rpc() and supabase.functions.invoke() with configurable retries.
 */

const DEFAULT_OPTS = {
  maxRetries: 3,
  baseDelay: 500,   // ms — doubles each retry (500, 1000, 2000)
  maxDelay: 5000,
  retryOn: (error) => {
    if (!error) return false;
    const msg = typeof error === "string" ? error : error.message || "";
    // Retry on network/timeout/rate-limit errors, not on validation or auth errors
    return /fetch|timeout|network|econnreset|socket|429|rate.?limit|502|503|504|unavailable/i.test(msg);
  },
};

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getBackoff(attempt, baseDelay, maxDelay) {
  const jitter = Math.random() * 0.3 + 0.85; // 0.85-1.15x randomization
  return Math.min(baseDelay * Math.pow(2, attempt) * jitter, maxDelay);
}

/**
 * Retry a Supabase RPC call.
 * Usage: await retryRpc(supabase, "place_order", { p_order_id: "123" })
 */
export async function retryRpc(supabase, fnName, params, opts = {}) {
  const { maxRetries, baseDelay, maxDelay, retryOn } = { ...DEFAULT_OPTS, ...opts };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const { data, error } = await supabase.rpc(fnName, params);

    if (!error) return { data, error: null };

    if (attempt < maxRetries && retryOn(error)) {
      await delay(getBackoff(attempt, baseDelay, maxDelay));
      continue;
    }

    return { data, error };
  }
}

/**
 * Retry a Supabase edge function invocation.
 * Usage: await retryInvoke(supabase, "send-order-confirmation", { body: {...} })
 */
export async function retryInvoke(supabase, fnName, payload, opts = {}) {
  const { maxRetries, baseDelay, maxDelay, retryOn } = { ...DEFAULT_OPTS, ...opts };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke(fnName, payload);

      if (!error) return { data, error: null };

      if (attempt < maxRetries && retryOn(error)) {
        await delay(getBackoff(attempt, baseDelay, maxDelay));
        continue;
      }

      return { data, error };
    } catch (err) {
      // functions.invoke can throw on network failures
      if (attempt < maxRetries && retryOn(err)) {
        await delay(getBackoff(attempt, baseDelay, maxDelay));
        continue;
      }
      throw err;
    }
  }
}
